// Credit-report provider seam.
//
// Phase 1: STUB. Every "ready" report returns the same sample payload so we can
// ship the full property management-request / tenant-consent / paid / view-report UX
// without a real bureau integration. When we sign up with a screening provider
// (TransUnion ShareAble, Experian Connect, RentPrep, etc.), replace the bodies
// of requestReport() and getReport() — the rest of the app calls only these
// two functions and a shared { status, report } shape.

import { supabase } from './supabase';

const SAMPLE_REPORT = {
  score: 724,
  scoreModel: 'VantageScore 3.0 (sample)',
  scoreBand: 'Good',
  pulledAt: null, // filled in at read time
  identity: {
    nameOnFile: 'Sample Tenant',
    addressesOnFile: ['123 Sample St, Apt 4, Springfield, IL'],
    ssnVerified: true,
  },
  summary: {
    openAccounts: 6,
    totalBalance: 12480,
    monthlyDebt: 410,
    utilization: 0.28,
    oldestAccountYears: 7.2,
    inquiriesLast2Yr: 2,
  },
  tradelines: [
    { creditor: 'Chase Sapphire',     type: 'Credit Card',      opened: '2018-03', balance: 1820,  limit: 8000,  status: 'Current' },
    { creditor: 'Capital One',        type: 'Credit Card',      opened: '2020-11', balance: 640,   limit: 3500,  status: 'Current' },
    { creditor: 'Discover',           type: 'Credit Card',      opened: '2017-06', balance: 0,     limit: 5000,  status: 'Current' },
    { creditor: 'Sallie Mae',         type: 'Student Loan',     opened: '2015-09', balance: 8420,  limit: null,  status: 'Current' },
    { creditor: 'Toyota Financial',   type: 'Auto Loan',        opened: '2022-01', balance: 1600,  limit: null,  status: 'Current' },
    { creditor: 'AmEx Blue',          type: 'Credit Card',      opened: '2019-08', balance: 0,     limit: 6000,  status: 'Closed by consumer' },
  ],
  publicRecords: [],     // bankruptcies, judgments, liens
  evictions: [],         // eviction filings / judgments
  collections: [],
  paymentHistory: { onTime: 96, late30: 2, late60: 0, late90Plus: 0 }, // %
};

// Create a request row. Called by the property management clicking "Request credit report"
// on a tenant profile. Real flow: also creates a Stripe Checkout Session and
// returns its URL.
//
// Returns { request, checkoutUrl }. checkoutUrl is null in Phase 1 (free).
export async function requestReport({ landlordId, tenantId, landlordName, tenantName }) {
  const { data, error } = await supabase
    .from('credit_report_requests')
    .insert({
      landlord_id:   landlordId,
      tenant_id:     tenantId,
      landlord_name: landlordName,
      tenant_name:   tenantName,
      status:        'pending_consent', // pending_consent | consented | ready | declined | expired
      // TODO: real flow stores stripe_session_id / paid_at here
    })
    .select()
    .single();
  if (error) throw error;
  return { request: data, checkoutUrl: null };
}

// Tenant clicks "I authorize" on the consent page.
// Real flow: kicks off the bureau pull (async, status flips to `ready` via webhook).
// Stub: flip straight to `ready` with the sample report attached.
export async function recordConsent({ requestId }) {
  const { data, error } = await supabase
    .from('credit_report_requests')
    .update({
      status:        'ready',
      consented_at:  new Date().toISOString(),
      report_json:   SAMPLE_REPORT,
      report_ready_at: new Date().toISOString(),
    })
    .eq('id', requestId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// Tenant explicitly declines.
export async function declineConsent({ requestId }) {
  const { data, error } = await supabase
    .from('credit_report_requests')
    .update({ status: 'declined', declined_at: new Date().toISOString() })
    .eq('id', requestId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// Property Management opens a report. Returns { status, report } — `report` is null until
// the request is `ready`.
export async function getReport({ requestId }) {
  const { data, error } = await supabase
    .from('credit_report_requests')
    .select('*')
    .eq('id', requestId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return { status: 'not_found', report: null, request: null };
  const report = data.status === 'ready'
    ? { ...(data.report_json || SAMPLE_REPORT), pulledAt: data.report_ready_at }
    : null;
  return { status: data.status, report, request: data };
}

export async function listLandlordRequests({ landlordId }) {
  const { data, error } = await supabase
    .from('credit_report_requests')
    .select('*')
    .eq('landlord_id', landlordId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export const STATUS_LABEL = {
  pending_consent: 'Awaiting tenant consent',
  consented:       'Pulling report…',
  ready:           'Ready to view',
  declined:        'Tenant declined',
  expired:         'Expired',
};

// Phase-1 sticker price we'll charge in Phase 2.
export const REPORT_PRICE_USD = 35;

export { SAMPLE_REPORT };
